import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Mail, MapPin } from "lucide-react";
import useNoIndex from "@/hooks/useNoIndex";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import { BOOKS_SUPPORT } from "@/data/books";
import { getPhysicalOrderConfirmation } from "@/lib/physicalOrders";

const ACCENT = "#F57F17";

/**
 * Physical-order confirmation page — Command 04 §22. Reachable ONLY with
 * the signed, order-scoped token issued by /store/physical/order/verify.
 * No download button here (this is a printed-book order, not a digital
 * entitlement) and no address/order data is ever shown without a valid
 * token (Command 04 §6/§31/§44.13).
 */
export default function ChinnuOrderConfirmed() {
  useNoIndex();
  const { orderRef } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  useTrackPageView("chinnu_print_order_confirmed_view", { order_ref: orderRef });

  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useSEO({
    title: "Order Confirmed — LifeIQ Chinnu",
    description: "Your Chinnu Talking Adventures printed-book order is confirmed.",
    canonicalPath: `/books/chinnu/order-confirmed/${orderRef}`,
  });

  useEffect(() => {
    getPhysicalOrderConfirmation(orderRef, token)
      .then((data) => {
        if (data.verified) {
          setOrder(data);
          setState("verified");
        } else {
          setState("locked");
        }
      })
      .catch(() => setState("locked"));
  }, [orderRef, token]);

  return (
    <main className="min-h-[70vh] bg-[#FBF7EE]">
      <section className="max-w-xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
        {state === "checking" && (
          <p data-testid="order-confirmed-checking" className="text-[14px] text-[#5C6470]">Confirming your order…</p>
        )}

        {state === "locked" && (
          <div data-testid="order-confirmed-locked">
            <div className="w-14 h-14 rounded-full bg-[#B3261E] text-white mx-auto flex items-center justify-center">
              <AlertTriangle size={26} />
            </div>
            <div className="mt-6 text-[12px] uppercase tracking-[0.24em] font-semibold text-[#B3261E]">Verification Needed</div>
            <h1 className="mt-4 font-serif-d text-[26px] sm:text-[30px] text-[#0D1B2A] leading-[1.15]">
              We couldn't verify this purchase.
            </h1>
            <p className="mt-3 text-[15px] text-[#5C6470] leading-[1.65]">
              If you have already paid, please contact LifeIQ support with your payment details.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[14px] text-[#0D1B2A]">
              <a data-testid="order-confirmed-support-email" href={`mailto:${BOOKS_SUPPORT.email}`} className="inline-flex items-center gap-2 underline">
                <Mail size={14} /> {BOOKS_SUPPORT.email}
              </a>
              <a data-testid="order-confirmed-support-whatsapp" href={BOOKS_SUPPORT.whatsapp_link} target="_blank" rel="noopener noreferrer" className="underline">
                WhatsApp {BOOKS_SUPPORT.whatsapp}
              </a>
            </div>
          </div>
        )}

        {state === "verified" && order && (
          <div data-testid="order-confirmed-verified">
            <div className="w-14 h-14 rounded-full text-white mx-auto flex items-center justify-center" style={{ backgroundColor: "#004D40" }}>
              <CheckCircle2 size={28} />
            </div>
            <div className="mt-6 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: ACCENT }}>Payment Successful</div>
            <h1 className="mt-4 font-serif-d text-[26px] sm:text-[32px] text-[#0D1B2A] leading-[1.15]">
              Thank you. Your Chinnu printed-book order is confirmed.
            </h1>

            <div className="mt-8 bg-white border border-[#E5E1D3] rounded-sm p-6 sm:p-7 text-left">
              <div className="flex justify-between text-[14px]"><span className="text-[#5C6470]">Order ID</span><span data-testid="order-confirmed-ref" className="font-mono font-semibold text-[#0D1B2A]">{order.order_ref}</span></div>
              <div className="flex justify-between text-[14px] mt-2"><span className="text-[#5C6470]">Purchased</span><span className="text-right text-[#0D1B2A]">{order.product_title}</span></div>
              <div className="flex justify-between text-[14px] mt-2"><span className="text-[#5C6470]">Amount Paid</span><span className="font-semibold text-[#0D1B2A]">₹{Math.round(order.total_amount / 100)}</span></div>
              <div className="mt-4 pt-4 border-t border-[#E5E7EA] flex items-start gap-2 text-[14px] text-[#0D1B2A]">
                <MapPin size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
                <span>Delivering to: {order.delivery.full_name}, {order.delivery.address_summary}</span>
              </div>
              <p className="mt-3 text-[13px] text-[#5C6470]">Confirmation email sent to: {order.delivery.email}</p>
            </div>

            <div className="mt-8 text-left">
              <div className="text-[12px] uppercase tracking-[0.2em] font-semibold text-[#5C6470]">What happens next?</div>
              <ol className="mt-3 space-y-2.5 text-[14px] text-[#0D1B2A]">
                <li className="flex gap-2.5"><span className="font-bold" style={{ color: ACCENT }}>1.</span> LifeIQ prepares your order.</li>
                <li className="flex gap-2.5"><span className="font-bold" style={{ color: ACCENT }}>2.</span> The order is packed for delivery.</li>
                <li className="flex gap-2.5"><span className="font-bold" style={{ color: ACCENT }}>3.</span> Dispatch/tracking details will be shared when available.</li>
              </ol>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/books" data-testid="order-confirmed-continue-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[12px] font-semibold uppercase tracking-wide rounded-sm text-white" style={{ backgroundColor: ACCENT }}>
                Continue Browsing LifeIQ Books <ArrowRight size={14} />
              </Link>
              <a href={`mailto:${BOOKS_SUPPORT.email}`} data-testid="order-confirmed-support-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[12px] font-semibold uppercase tracking-wide rounded-sm border border-[#0D1B2A] text-[#0D1B2A]">
                Contact Support
              </a>
            </div>
            <Link to="/books/chinnu/track-order" data-testid="order-confirmed-track-link" className="mt-5 inline-block text-[12px] uppercase tracking-wide font-semibold underline" style={{ color: ACCENT }}>
              Track this order later →
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
